import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { BudgetIncreaseModal } from '@/components/recommendations/BudgetIncreaseModal';
import { KeywordApplyConfirmModal } from '@/components/recommendations/KeywordApplyConfirmModal';
import { PauseConfirmModal } from '@/components/recommendations/PauseConfirmModal';
import { TosBidIncreaseModal } from '@/components/recommendations/TosBidIncreaseModal';
import { ChokedAsinsTable } from '@/components/recommendations/ruleTables/ChokedAsinsTable';
import { LowTosTable } from '@/components/recommendations/ruleTables/LowTosTable';
import { MissedSpendTable } from '@/components/recommendations/ruleTables/MissedSpendTable';
import { PoorCampaignsTable } from '@/components/recommendations/ruleTables/PoorCampaignsTable';
import { PoorKeywordsTable } from '@/components/recommendations/ruleTables/PoorKeywordsTable';
import { RuleTableCard } from '@/components/recommendations/ruleTables/RuleTableCard';
import { useToast } from '@/components/ui/Toast';
import { adsApi } from '@/api/client';
import { reportAdProductsQueryParam, REPORT_AD_PRODUCT_OPTIONS } from '@/api/reportAdProducts';
import type { DateRange } from '@/api/reportDateRanges';
import {
  dismissRecommendations,
  filterDismissedRows,
  loadDismissedRecommendations,
  ruleRecommendationFingerprint,
} from '@/api/recommendationDismiss';
import { filterBySearch, sortByMetric, type RecommendationSortField, type RecommendationSortOrder } from '@/api/recommendationUtils';
import {
  DEFAULT_RULE_THRESHOLDS,
  labelForDateRange,
  parseRuleRecommendations,
  toShortAdProduct,
} from '@/api/ruleRecommendations';
import type { CampaignActionRow } from '@/types/campaignRecommendations';
import type { SpKeywordApplyAction } from '@/types/keywordRecommendations';
import type { RuleAsinRow, RuleCampaignRow, RuleKeywordRow, RuleThresholds } from '@/types/ruleRecommendations';

function emptySort(): {
  search: string;
  sortBy: RecommendationSortField;
  sortOrder: RecommendationSortOrder;
} {
  return { search: '', sortBy: 'cost', sortOrder: 'desc' };
}

export function RuleRecommendationTables({
  profileId,
  range,
  rangeOk,
}: {
  profileId: string;
  range: DateRange;
  rangeOk: boolean;
}) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [thresholds, setThresholds] = useState<RuleThresholds>(DEFAULT_RULE_THRESHOLDS);
  const [dismissed, setDismissed] = useState(() => loadDismissedRecommendations(profileId));
  const [missedControls, setMissedControls] = useState(emptySort);
  const [tosControls, setTosControls] = useState(emptySort);
  const [keywordControls, setKeywordControls] = useState(emptySort);
  const [poorCampaignControls, setPoorCampaignControls] = useState(emptySort);
  const [asinControls, setAsinControls] = useState(emptySort);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [budgetRow, setBudgetRow] = useState<CampaignActionRow | null>(null);
  const [pauseRow, setPauseRow] = useState<CampaignActionRow | null>(null);
  const [tosRow, setTosRow] = useState<RuleCampaignRow | null>(null);
  const [pendingApply, setPendingApply] = useState<SpKeywordApplyAction[] | null>(null);

  const rangeLabel = labelForDateRange(range);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'rule-recommendation-tables',
      profileId,
      range.startDate,
      range.endDate,
      thresholds,
    ],
    queryFn: async () => {
      const res = await adsApi.getRuleRecommendationTables(profileId, {
        startDate: range.startDate,
        endDate: range.endDate,
        adProducts: reportAdProductsQueryParam(REPORT_AD_PRODUCT_OPTIONS.map((option) => option.id)),
        ...thresholds,
      });
      return parseRuleRecommendations(res.data);
    },
    enabled: !!profileId && rangeOk,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['rule-recommendation-tables', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['campaign-recommendations', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['keyword-recommendations', profileId] });
  };

  const applyMutation = useMutation({
    mutationFn: async (actions: SpKeywordApplyAction[]) => {
      const res = await adsApi.applySpKeywords(profileId, { actions });
      if (!res.success) throw new Error('Failed to apply keyword actions');
      return res;
    },
    onSuccess: () => {
      showToast('Keyword actions applied.', 'success');
      setPendingApply(null);
      setSelectedKeywords(new Set());
      invalidate();
    },
    onError: (err) => {
      showToast((err as Error).message || 'Failed to apply keyword actions', 'error');
    },
  });

  const tables = data?.tables;
  const missedRows = useMemo(() => {
    const rows = filterDismissedRows(
      tables?.missedSpend?.rows ?? [],
      dismissed,
      (row) => ruleRecommendationFingerprint('missedSpend', row.campaignId)
    );
    const searched = filterBySearch(rows, missedControls.search, (row) => [
      row.campaignName,
      row.campaignId,
    ]);
    return sortByMetric(searched, missedControls.sortBy, missedControls.sortOrder, (row) => ({
      cost: row.opportunitySpend ?? row.cost,
      clicks: row.clicks,
      roas: row.roas,
      sales30d: row.opportunitySales ?? row.sales30d,
    }));
  }, [tables?.missedSpend?.rows, dismissed, missedControls]);

  const tosRows = useMemo(() => {
    const rows = filterDismissedRows(
      tables?.lowTos?.rows ?? [],
      dismissed,
      (row) => ruleRecommendationFingerprint('lowTos', row.campaignId)
    );
    const searched = filterBySearch(rows, tosControls.search, (row) => [
      row.campaignName,
      row.campaignId,
    ]);
    return sortByMetric(searched, tosControls.sortBy, tosControls.sortOrder, (row) => ({
      cost: row.cost,
      clicks: row.clicks,
      roas: row.roas,
      sales30d: row.sales30d,
    }));
  }, [tables?.lowTos?.rows, dismissed, tosControls]);

  const keywordRows = useMemo(() => {
    const rows = filterDismissedRows(
      tables?.poorKeywords?.rows ?? [],
      dismissed,
      (row) => ruleRecommendationFingerprint('poorKeywords', row.keywordId)
    );
    const searched = filterBySearch(rows, keywordControls.search, (row) => [
      row.keywordText,
      row.campaignName,
      row.keywordId,
    ]);
    return sortByMetric(searched, keywordControls.sortBy, keywordControls.sortOrder, (row) => ({
      cost: row.cost,
      clicks: row.clicks,
      roas: row.roas,
      sales30d: row.sales30d,
    }));
  }, [tables?.poorKeywords?.rows, dismissed, keywordControls]);

  const poorCampaignRows = useMemo(() => {
    const rows = filterDismissedRows(
      tables?.poorCampaigns?.rows ?? [],
      dismissed,
      (row) => ruleRecommendationFingerprint('poorCampaigns', row.campaignId)
    );
    const searched = filterBySearch(rows, poorCampaignControls.search, (row) => [
      row.campaignName,
      row.campaignId,
    ]);
    return sortByMetric(
      searched,
      poorCampaignControls.sortBy,
      poorCampaignControls.sortOrder,
      (row) => ({
        cost: row.cost,
        clicks: row.clicks,
        roas: row.roas,
        sales30d: row.sales30d,
      })
    );
  }, [tables?.poorCampaigns?.rows, dismissed, poorCampaignControls]);

  const asinRows = useMemo(() => {
    const rows = filterDismissedRows(
      tables?.chokedAsins?.rows ?? [],
      dismissed,
      (row) => ruleRecommendationFingerprint('chokedAsins', row.asin)
    );
    const searched = filterBySearch(rows, asinControls.search, (row) => [
      row.asin,
      row.productName,
      row.campaignName,
    ]);
    return sortByMetric(searched, asinControls.sortBy, asinControls.sortOrder, (row) => ({
      cost: row.cost,
      clicks: row.purchases30d,
      roas: row.roas,
      sales30d: row.sales30d,
    }));
  }, [tables?.chokedAsins?.rows, dismissed, asinControls]);

  const dismissEntity = (tableId: string, entityId: string) => {
    setDismissed(dismissRecommendations(profileId, [ruleRecommendationFingerprint(tableId, entityId)]));
  };

  const campaignActionRow = (row: RuleCampaignRow | RuleAsinRow): CampaignActionRow => ({
    campaignId: row.campaignId,
    campaignName: row.campaignName,
    campaignType: row.campaignType,
    adProduct: row.adProduct,
    dailyBudget: row.dailyBudget,
    budgetType: row.budgetType,
    suggestedBudget: row.suggestedBudget,
    budgetIncreasePct: row.budgetIncreasePct,
    projectedAdditionalEarnings:
      'projectedAdditionalEarnings' in row ? row.projectedAdditionalEarnings : undefined,
    projectedAdditionalSales:
      'projectedAdditionalSales' in row ? row.projectedAdditionalSales : undefined,
    projectedSavings: 'projectedSavings' in row ? row.projectedSavings : undefined,
    projectedLossAvoided: 'projectedLossAvoided' in row ? row.projectedLossAvoided : undefined,
    cost: row.cost,
  });

  const decreaseAction = (row: RuleKeywordRow): SpKeywordApplyAction => ({
    action: 'DECREASE_BID',
    keywordId: row.keywordId,
    bid: row.recommendedBid,
  });

  const totalVisible =
    missedRows.length +
    tosRows.length +
    keywordRows.length +
    poorCampaignRows.length +
    asinRows.length;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-headline-md text-foreground">Rule-based opportunities</h2>
        <span className="font-mono text-body-sm text-foreground-subtle">
          {isLoading ? '…' : `${totalVisible} rows`}
        </span>
      </div>
      <p className="text-body-md text-foreground-muted">
        Tables below use fixed performance rules for the selected date range. Edit a table&apos;s rules
        for this session, then act on a row without leaving the page.
      </p>

      {!rangeOk ? (
        <p className="rounded-xl border border-warning/20 bg-warning-subtle px-3 py-2 text-sm text-warning-ink">
          Select a valid date range.
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-4 py-6 text-sm text-foreground-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading opportunity tables…
        </div>
      ) : null}

      {isError ? (
        <div className="rounded-xl border border-warning/20 bg-warning-subtle px-4 py-3 text-sm text-warning-ink">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p>{(error as Error)?.message || 'Failed to load opportunity tables.'}</p>
              <button type="button" className="mt-1 text-brand hover:underline" onClick={() => void refetch()}>
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!isLoading && !isError && rangeOk ? (
        <div className="space-y-4">
          <RuleTableCard
            tableId="missedSpend"
            title={tables?.missedSpend?.title || 'Good campaigns with missed spend'}
            count={missedRows.length}
            chips={tables?.missedSpend?.logic ?? []}
            rangeLabel={rangeLabel}
            note="Opportunity spend is 20% of allocated budget for campaigns already at ≥95% utilization."
            thresholds={thresholds}
            onThresholdsChange={setThresholds}
            onResetThresholds={() => setThresholds(DEFAULT_RULE_THRESHOLDS)}
            searchQuery={missedControls.search}
            onSearchChange={(search) => setMissedControls((current) => ({ ...current, search }))}
            sortBy={missedControls.sortBy}
            sortOrder={missedControls.sortOrder}
            onSortByChange={(sortBy) => setMissedControls((current) => ({ ...current, sortBy }))}
            onSortOrderChange={(sortOrder) => setMissedControls((current) => ({ ...current, sortOrder }))}
            showingCount={missedRows.length}
            totalCount={tables?.missedSpend?.rowCount ?? missedRows.length}
            defaultExpanded={missedRows.length > 0}
          >
            <MissedSpendTable
              profileId={profileId}
              rows={missedRows}
              onIncreaseBudget={(row) => setBudgetRow(campaignActionRow(row))}
              onDismiss={(row) => dismissEntity('missedSpend', row.campaignId)}
            />
          </RuleTableCard>

          <RuleTableCard
            tableId="lowTos"
            title={tables?.lowTos?.title || 'Good campaigns with low impressions and low TOS'}
            count={tosRows.length}
            chips={tables?.lowTos?.logic ?? []}
            rangeLabel={rangeLabel}
            thresholds={thresholds}
            onThresholdsChange={setThresholds}
            onResetThresholds={() => setThresholds(DEFAULT_RULE_THRESHOLDS)}
            searchQuery={tosControls.search}
            onSearchChange={(search) => setTosControls((current) => ({ ...current, search }))}
            sortBy={tosControls.sortBy}
            sortOrder={tosControls.sortOrder}
            onSortByChange={(sortBy) => setTosControls((current) => ({ ...current, sortBy }))}
            onSortOrderChange={(sortOrder) => setTosControls((current) => ({ ...current, sortOrder }))}
            showingCount={tosRows.length}
            totalCount={tables?.lowTos?.rowCount ?? tosRows.length}
            defaultExpanded={tosRows.length > 0}
          >
            <LowTosTable
              profileId={profileId}
              rows={tosRows}
              onRaiseTos={setTosRow}
              onDismiss={(row) => dismissEntity('lowTos', row.campaignId)}
            />
          </RuleTableCard>

          <RuleTableCard
            tableId="poorKeywords"
            title={tables?.poorKeywords?.title || 'Poor-performing keywords'}
            count={keywordRows.length}
            chips={tables?.poorKeywords?.logic ?? []}
            rangeLabel={rangeLabel}
            thresholds={thresholds}
            onThresholdsChange={setThresholds}
            onResetThresholds={() => setThresholds(DEFAULT_RULE_THRESHOLDS)}
            searchQuery={keywordControls.search}
            onSearchChange={(search) => setKeywordControls((current) => ({ ...current, search }))}
            sortBy={keywordControls.sortBy}
            sortOrder={keywordControls.sortOrder}
            onSortByChange={(sortBy) => setKeywordControls((current) => ({ ...current, sortBy }))}
            onSortOrderChange={(sortOrder) =>
              setKeywordControls((current) => ({ ...current, sortOrder }))
            }
            showingCount={keywordRows.length}
            totalCount={tables?.poorKeywords?.rowCount ?? keywordRows.length}
            defaultExpanded={keywordRows.length > 0}
          >
            <PoorKeywordsTable
              profileId={profileId}
              rows={keywordRows}
              selected={selectedKeywords}
              applying={applyMutation.isPending}
              onToggleSelect={(id) => {
                setSelectedKeywords((current) => {
                  const next = new Set(current);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              }}
              onSelectAll={(ids) => setSelectedKeywords(new Set(ids))}
              onDecreaseBid={(row) => setPendingApply([decreaseAction(row)])}
              onPause={(row) => setPendingApply([{ action: 'PAUSE', keywordId: row.keywordId }])}
              onDismiss={(row) => {
                dismissEntity('poorKeywords', row.keywordId);
                setSelectedKeywords((current) => {
                  const next = new Set(current);
                  next.delete(row.keywordId);
                  return next;
                });
              }}
              onBulkApply={() => {
                const actions = keywordRows
                  .filter((row) => selectedKeywords.has(row.keywordId))
                  .map(decreaseAction);
                if (actions.length) setPendingApply(actions);
              }}
              onBulkDismiss={() => {
                const ids = [...selectedKeywords];
                setDismissed(
                  dismissRecommendations(
                    profileId,
                    ids.map((id) => ruleRecommendationFingerprint('poorKeywords', id))
                  )
                );
                setSelectedKeywords(new Set());
              }}
              onClearSelection={() => setSelectedKeywords(new Set())}
            />
          </RuleTableCard>

          <RuleTableCard
            tableId="poorCampaigns"
            title={tables?.poorCampaigns?.title || 'Poor-performing campaigns'}
            count={poorCampaignRows.length}
            chips={tables?.poorCampaigns?.logic ?? []}
            rangeLabel={rangeLabel}
            thresholds={thresholds}
            onThresholdsChange={setThresholds}
            onResetThresholds={() => setThresholds(DEFAULT_RULE_THRESHOLDS)}
            searchQuery={poorCampaignControls.search}
            onSearchChange={(search) =>
              setPoorCampaignControls((current) => ({ ...current, search }))
            }
            sortBy={poorCampaignControls.sortBy}
            sortOrder={poorCampaignControls.sortOrder}
            onSortByChange={(sortBy) =>
              setPoorCampaignControls((current) => ({ ...current, sortBy }))
            }
            onSortOrderChange={(sortOrder) =>
              setPoorCampaignControls((current) => ({ ...current, sortOrder }))
            }
            showingCount={poorCampaignRows.length}
            totalCount={tables?.poorCampaigns?.rowCount ?? poorCampaignRows.length}
            defaultExpanded={poorCampaignRows.length > 0}
          >
            <PoorCampaignsTable
              profileId={profileId}
              rows={poorCampaignRows}
              onPause={(row) => setPauseRow(campaignActionRow(row))}
              onDismiss={(row) => dismissEntity('poorCampaigns', row.campaignId)}
            />
          </RuleTableCard>

          <RuleTableCard
            tableId="chokedAsins"
            title={tables?.chokedAsins?.title || 'High-rated ASINs with low ad spend'}
            count={asinRows.length}
            chips={tables?.chokedAsins?.logic ?? []}
            rangeLabel={rangeLabel}
            note={tables?.chokedAsins?.note}
            thresholds={thresholds}
            onThresholdsChange={setThresholds}
            onResetThresholds={() => setThresholds(DEFAULT_RULE_THRESHOLDS)}
            searchQuery={asinControls.search}
            onSearchChange={(search) => setAsinControls((current) => ({ ...current, search }))}
            sortBy={asinControls.sortBy}
            sortOrder={asinControls.sortOrder}
            onSortByChange={(sortBy) => setAsinControls((current) => ({ ...current, sortBy }))}
            onSortOrderChange={(sortOrder) => setAsinControls((current) => ({ ...current, sortOrder }))}
            showingCount={asinRows.length}
            totalCount={tables?.chokedAsins?.rowCount ?? asinRows.length}
            defaultExpanded={asinRows.length > 0}
          >
            <ChokedAsinsTable
              profileId={profileId}
              rows={asinRows}
              onIncreaseBudget={(row) => setBudgetRow(campaignActionRow(row))}
              onDismiss={(row) => dismissEntity('chokedAsins', row.asin)}
            />
          </RuleTableCard>
        </div>
      ) : null}

      {budgetRow ? (
        <BudgetIncreaseModal
          profileId={profileId}
          row={budgetRow}
          budgetIncreasePct={budgetRow.budgetIncreasePct ?? thresholds.budgetIncreasePct}
          isOpen
          onClose={() => setBudgetRow(null)}
          onSuccess={invalidate}
          onToast={showToast}
        />
      ) : null}
      {pauseRow ? (
        <PauseConfirmModal
          profileId={profileId}
          row={pauseRow}
          isOpen
          onClose={() => setPauseRow(null)}
          onSuccess={invalidate}
          onToast={showToast}
        />
      ) : null}
      {tosRow ? (
        <TosBidIncreaseModal
          profileId={profileId}
          campaignId={tosRow.campaignId}
          campaignName={tosRow.campaignName}
          adProduct={toShortAdProduct(tosRow.adProduct, tosRow.campaignType)}
          currentTosPercent={tosRow.tosBidPercent}
          isOpen
          onClose={() => setTosRow(null)}
          onSuccess={invalidate}
          onToast={showToast}
        />
      ) : null}
      {pendingApply ? (
        <KeywordApplyConfirmModal
          actions={pendingApply}
          pending={applyMutation.isPending}
          onConfirm={() => applyMutation.mutate(pendingApply)}
          onCancel={() => setPendingApply(null)}
        />
      ) : null}
    </section>
  );
}
